// The documents board: what came in, what it was read as, and the acts that turn
// a piece of paper into evidence.
//
// WHY THERE IS A BULK BAR AT ALL, AND WHY IT IS NARROWER THAN IT LOOKS.
//
// Forty slips can now go in at once, so forty rows come out, and a board where
// each one needs three fields typed is the same bottleneck moved one step later.
// Two of the three are usually already right: the reader proposes the date and the
// amount off the document. What it cannot propose is whose it is, and that is the
// one field that decides whether a man is recorded as having paid in.
//
// So the bulk bar does the boring part in one go and refuses to do the careful
// part blind:
//
//   ASSIGN A DRIVER to everything selected. This writes no verdict. It says these
//   forty slips came out of Sipho's envelope, which is exactly what a manager
//   knows when he is holding the envelope.
//
//   CONFIRM everything selected, and ONLY rows that already carry a driver, a date
//   and an amount. Anything short of that is listed by name and left alone rather
//   than being filled in with a guess. Before it runs, the bar states what is about
//   to be asserted: how many documents, for how many named men, totalling what.
//   Confirming is the moment somebody stands behind a figure about another man's
//   money, and a button that does that without saying whose money is a button that
//   will eventually be pressed by accident.
//
//   REJECT everything selected, with one written reason, because a batch is
//   usually rejected for one reason: the photographs are unreadable.
import { useMemo, useState } from 'react'
import { AlertCircle, Check, FileText, Users } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Chip } from '../interviews/Chip'
import { zar, num } from './cashModel'
import type { DriverOption } from './CashDocumentUpload'

type Row = Record<string, any>

export type ConfirmArgs = {
  document_id: string
  driver_id: string
  doc_date: string
  amount_zar: number
  reference?: string
}

function statusTone(status: string): 'green' | 'red' | 'amber' | 'neutral' {
  if (status === 'confirmed') return 'green'
  if (status === 'rejected') return 'red'
  if (status === 'read') return 'amber'
  return 'neutral'
}

function statusLabel(status: string): string {
  if (status === 'confirmed') return 'confirmed'
  if (status === 'rejected') return 'rejected'
  if (status === 'read') return 'read, not confirmed'
  return 'not read'
}

/** A row is ready to confirm when nothing has to be invented to do it. */
export function isReady(d: Row): boolean {
  return Boolean(d.driver_id) && Boolean(d.doc_date) && Number(d.amount_zar) > 0
}

export function BulkBar({
  selected,
  drivers,
  busy,
  progress,
  onAssign,
  onConfirm,
  onReject,
  onClear,
}: {
  selected: Row[]
  drivers: DriverOption[]
  busy: boolean
  progress: string | null
  onAssign: (driverId: string) => void
  onConfirm: (rows: Row[]) => void
  onReject: (reason: string) => void
  onClear: () => void
}) {
  const [assignTo, setAssignTo] = useState('')

  const ready = useMemo(() => selected.filter(isReady), [selected])
  const notReady = useMemo(() => selected.filter((d) => !isReady(d)), [selected])
  const total = ready.reduce((s, d) => s + Number(d.amount_zar ?? 0), 0)
  const men = new Set(ready.map((d) => String(d.driver_id))).size

  if (selected.length === 0) return null

  return (
    <div className="sticky bottom-0 z-10 space-y-2 rounded-md border border-border bg-card p-3 shadow-lg">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="font-medium text-foreground">{num(selected.length)} selected</span>
        <button type="button" className="underline text-muted-foreground" onClick={onClear}>
          clear
        </button>
        {progress && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Spinner className="h-3 w-3" /> {progress}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label className="text-[11px]">Say whose these are</Label>
          <div className="flex gap-2">
            <NativeSelect
              className="w-56"
              value={assignTo}
              disabled={busy}
              onChange={(e) => setAssignTo(e.target.value)}
            >
              <option value="">Choose a driver</option>
              {drivers.map((d) => (
                <option key={d.driver_id} value={d.driver_id}>
                  {d.driver_name}
                </option>
              ))}
            </NativeSelect>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || assignTo === ''}
              onClick={() => onAssign(assignTo)}
            >
              <Users className="mr-1 h-3.5 w-3.5" /> Assign
            </Button>
          </div>
        </div>

        <Button
          size="sm"
          disabled={busy || ready.length === 0}
          onClick={() => onConfirm(ready)}
        >
          <Check className="mr-1 h-3.5 w-3.5" />
          Confirm {ready.length}
        </Button>

        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => {
            const reason = window.prompt(
              `Why are these ${selected.length} documents being rejected? The drivers who handed them in are owed that sentence.`,
            )
            if (reason) onReject(reason)
          }}
        >
          Reject {selected.length}
        </Button>
      </div>

      {ready.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          About to assert that {num(ready.length)} document{ready.length === 1 ? '' : 's'} totalling{' '}
          <span className="tabular-nums font-medium text-foreground">{zar(total)}</span>{' '}
          {ready.length === 1 ? 'belongs' : 'belong'} to {num(men)} named{' '}
          {men === 1 ? 'driver' : 'drivers'}, on the dates and amounts shown on each row. Nothing is
          deducted from anybody and no salary changes.
        </p>
      )}
      {notReady.length > 0 && (
        <p className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-300">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          {num(notReady.length)} of the selected {notReady.length === 1 ? 'has' : 'have'} no driver,
          no date or no amount and {notReady.length === 1 ? 'is' : 'are'} left alone. Fill those in
          on the row, or assign a driver above: nothing here guesses a figure.
        </p>
      )}
    </div>
  )
}

export function DocumentRow({
  doc,
  drivers,
  selected,
  selectable,
  busy,
  onSelect,
  onConfirm,
  onReject,
  onOpen,
}: {
  doc: Row
  drivers: DriverOption[]
  selected: boolean
  selectable: boolean
  busy: boolean
  onSelect: (checked: boolean) => void
  onConfirm: (args: ConfirmArgs) => void
  onReject: (args: { document_id: string; reason: string }) => void
  onOpen: (documentId: string) => void
}) {
  const [driverId, setDriverId] = useState(String(doc.driver_id ?? ''))
  const [docDate, setDocDate] = useState(String(doc.doc_date ?? ''))
  const [amount, setAmount] = useState(doc.amount_zar != null ? String(doc.amount_zar) : '')

  const open = doc.status === 'read' || doc.status === 'uploaded'
  const ready = driverId !== '' && docDate !== '' && Number(amount) > 0

  return (
    <li className="rounded-md border border-border">
      <div className="flex flex-wrap items-baseline gap-2 p-3">
        {selectable && (
          <Checkbox
            checked={selected}
            disabled={busy}
            onCheckedChange={(c) => onSelect(c === true)}
            aria-label={`Select ${doc.file_name}`}
            className="mt-0.5"
          />
        )}
        <span className="text-sm font-medium text-foreground">
          {doc.kind === 'deposit_slip'
            ? 'Deposit slip'
            : doc.kind === 'fuel_receipt'
              ? 'Petrol receipt'
              : 'Document'}
        </span>
        <span className="text-xs text-muted-foreground">
          {doc.driver_name || 'not attributed'} · {doc.file_name}
        </span>
        <Chip tone={statusTone(String(doc.status))} className="ml-auto">
          {statusLabel(String(doc.status))}
        </Chip>
        <span className="text-xs text-muted-foreground">
          {doc.source === 'manager_upload' ? 'uploaded by a manager' : 'submitted by the driver'}
        </span>
      </div>
      <p className="px-3 pb-2 text-xs text-muted-foreground">{doc.note}</p>

      {doc.status === 'confirmed' && (
        <p className="px-3 pb-3 text-xs">
          <span className="tabular-nums font-medium">{zar(doc.amount_zar)}</span>{' '}
          <span className="text-muted-foreground">
            on {String(doc.doc_date)} · confirmed by {doc.confirmed_by_name}
          </span>
        </p>
      )}

      {open && selectable && (
        <div className="grid gap-2 border-t border-border p-3 sm:grid-cols-[1fr_auto]">
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-[11px]">Driver</Label>
              <NativeSelect value={driverId} disabled={busy} onChange={(e) => setDriverId(e.target.value)}>
                <option value="">Whose is it</option>
                {drivers.map((d) => (
                  <option key={d.driver_id} value={d.driver_id}>
                    {d.driver_name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Date on the document</Label>
              <Input type="date" value={docDate} disabled={busy} onChange={(e) => setDocDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px]">Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={amount}
                disabled={busy}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button size="sm" variant="outline" onClick={() => onOpen(String(doc.document_id))}>
              <FileText className="mr-1 h-3.5 w-3.5" /> Look at it
            </Button>
            <Button
              size="sm"
              disabled={!ready || busy}
              onClick={() =>
                onConfirm({
                  document_id: String(doc.document_id),
                  driver_id: driverId,
                  doc_date: docDate,
                  amount_zar: Number(amount),
                })
              }
            >
              <Check className="mr-1 h-3.5 w-3.5" /> Confirm
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                const reason = window.prompt(
                  'Why is this document being rejected? The driver who handed it in is owed that sentence.',
                )
                if (reason) onReject({ document_id: String(doc.document_id), reason })
              }}
            >
              Reject
            </Button>
          </div>
        </div>
      )}
    </li>
  )
}
